import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

/**
 * Drop-in replacement for react-oidc-context's useAuth().
 * Reads the access token from sessionStorage (set by OAuthCallback after Amplify sign-in)
 * and proactively refreshes it on mount via Amplify's fetchAuthSession().
 *
 * getToken() is the async method for event handlers and mutation functions.
 * It calls fetchAuthSession() so Amplify can silently refresh an expired access
 * token using the stored refresh token — preventing 401s in long-running sessions.
 */
export function useAuth() {
  const [accessToken, setAccessToken] = useState(() => sessionStorage.getItem("access_token") ?? "");

  // Proactively refresh on mount so components re-render with a fresh token, AND
  // subscribe to Amplify auth events so long-lived, root-mounted consumers pick
  // up a sign-in that happens *after* mount without a page reload.
  //
  // Root providers (e.g. StudentProfileProvider) mount once at app boot — before
  // the OAuth redirect flow has established a session — so the mount-time
  // fetchAuthSession() below resolves to no token and `accessToken` stays "".
  // OAuthCallback then signs in and navigates (a route change, not a remount), so
  // without the Hub listener this hook's `accessToken` state would never update
  // and `isAuthenticated` would remain false until a manual reload — starving any
  // isAuthenticated-gated effect (the student profile fetch) of its trigger.
  useEffect(() => {
    let cancelled = false;

    const syncToken = () =>
      fetchAuthSession()
        .then(session => {
          if (cancelled) return;
          const fresh = session.tokens?.accessToken?.toString() ?? '';
          if (fresh) {
            sessionStorage.setItem('access_token', fresh);
            setAccessToken(fresh);
          }
        })
        .catch(() => { /* not authenticated — ignore */ });

    syncToken();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
        case 'tokenRefresh':
          syncToken();
          break;
        case 'signedOut':
          if (!cancelled) {
            sessionStorage.removeItem('access_token');
            setAccessToken('');
          }
          break;
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  /**
   * Async token getter for use inside event handlers and mutation functions.
   * Calls Amplify's fetchAuthSession() which transparently refreshes the access
   * token via the stored refresh token when it is expired or about to expire.
   * Falls back to the cached sessionStorage value if Amplify is unavailable.
   */
  const getToken = useCallback(async (): Promise<string> => {
    try {
      const session = await fetchAuthSession();
      const fresh = session.tokens?.accessToken?.toString() ?? '';
      if (fresh) {
        sessionStorage.setItem('access_token', fresh);
        return fresh;
      }
    } catch { /* ignore */ }
    return sessionStorage.getItem('access_token') ?? '';
  }, []); // stable — fetchAuthSession is a stable Amplify singleton

  const isAuthenticated = !!accessToken;

  return {
    isAuthenticated,
    isLoading: false,
    user: accessToken ? { access_token: accessToken } : null,
    getToken,
  };
}
